import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { errorText, fetchHistory } from '../ptlSetupApi'
import { COUNT_LABEL, ZONE_MODES, actionLabel, fmtDate, num } from '../constants'

const PAGE = 20

const countsText = (counts = {}) => Object.keys(COUNT_LABEL)
  .filter((k) => counts[k])
  .map((k) => `${COUNT_LABEL[k]} ${num(counts[k])}`)
  .join(' · ') || '—'

/**
 * Change history of a zone map or TAT from the audit log (who, when, file, counts). Read only: old versions are not
 * kept, so there is nothing to download or restore.
 */
export default function HistoryDialog({ open, onClose, kind, merchantId, targetLabel }) {
  const [rows, setRows] = useState([])
  const [more, setMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback((page) => {
    setLoading(true)
    fetchHistory(kind, merchantId, page, PAGE)
      .then((list) => {
        setRows((cur) => (page === 0 ? list : [...cur, ...list]))
        setMore(list.length === PAGE)
      })
      .catch((e) => setError(errorText(e, 'Could not load the history')))
      .finally(() => setLoading(false))
  }, [kind, merchantId])

  useEffect(() => {
    if (open) { setError(''); load(0) }
  }, [open, load])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>History · {targetLabel}</DialogTitle>
      <DialogContent>
        {error && <Alert severity='error' sx={{ mb: 2, fontSize: 13 }}>{error}</Alert>}
        {!loading && rows.length === 0 && !error && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No changes yet.</Typography>
        )}
        {rows.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['When', 'What', 'File', 'Changes', 'By'].map((h) => <TableCell key={h} sx={{ fontSize: 12, fontWeight: 600 }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((v, i) => (
                  <TableRow key={`${v.createdAt}-${i}`}>
                    <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(v.createdAt)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {actionLabel(v.action)}
                      {v.mode ? <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{ZONE_MODES[v.mode]?.label || v.mode}</Typography> : null}
                      {v.error ? <Typography sx={{ fontSize: 11, color: 'error.main', maxWidth: 280, overflowWrap: 'anywhere' }}>{v.error}</Typography> : null}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 200, wordBreak: 'break-all' }}>{v.fileName || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{countsText(v.counts)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.createdBy || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {more && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <LoadingButton size='small' loading={loading} onClick={() => load(Math.ceil(rows.length / PAGE))} sx={{ textTransform: 'none' }}>Load older changes</LoadingButton>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
